import React from "react"

import { useCustomerInvitationListSuspense } from "@boundless/bl-api-client/customer/services"
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"

import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { customerReferralColumns } from "@/lib/data-tables/customer-referral-columns"

import type { CustomerInvitation } from "@boundless/bl-api-client/customer/models"

export function CustomerReferralsTable() {
  const [sorting] = React.useState([{ id: "createdAt", desc: true }])
  const { data: invitationList } = useCustomerInvitationListSuspense()

  const table = useReactTable<CustomerInvitation>({
    data: invitationList,
    columns: customerReferralColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  })

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <TableHead key={header.id} className="w-40">
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              )
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell
              colSpan={customerReferralColumns.length}
              className="h-24 text-center"
            >
              You have no referrals yet.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}

export function FallbackReferralsTable() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Company</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Funding Requests</TableHead>
          <TableHead>Created</TableHead>
          <TableHead>Updated</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 3 }).map((_, i) => (
          <TableRow key={i}>
            {Array.from({ length: customerReferralColumns.length }).map(
              (_, j) => (
                <TableCell key={j}>
                  <Skeleton className="h-6" />
                </TableCell>
              ),
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
